import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import SvgIcon from '@mui/material/SvgIcon';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as SidebarCrewMgmtIcon } from '../assets/icons/sidebar_crew_management.svg';
import RankStar from './RankStar';
import { crewApi } from '../services/api';
import { CrewRanksSummary } from '../types';
import { useAuth } from '../auth/AuthContext';
import { useManagementAuth } from '../auth/ManagementAuthContext';
import { getRankTierLabel } from '../constants/rankColors';
import { useCrewRankTiers } from '../hooks/useCrewRankTiers';

const PRIMARY_BLUE = '#0071e3';
const ADVICE_BADGE_PURPLE = '#673FC8';
const COPILOT_PURPLE = '#6A3DC2';
const COPILOT_PILL = '#534DFF';

/**
 * ホーム画面の「クルー管理」サマリ（PDF p.3）。
 * RECRUITING COPILOT 育成アドバイスカード ＋ ランク別人数タイル（CrewManagement と同データ源）。
 *
 * データは GET /store/{id}/crew-ranks/summary（crewApi.getCrewRanksSummary）。
 * ゲーミフィケーション無効（503）等で取得できない場合はセクションごと非表示にして
 * ホーム本体（データ等）を壊さない。
 */
const CrewRankSummary: React.FC = () => {
  const navigate = useNavigate();
  const { storeId } = useAuth();
  const { managementToken } = useManagementAuth();
  // ProtectedRoutes 配下で管理トークンは Layout の自動ログインで設定される（CrewManagement と同パターン）。
  const token = managementToken || localStorage.getItem('management_token') || '';

  const [summary, setSummary] = useState<CrewRanksSummary | null>(null);

  useEffect(() => {
    let active = true;
    const fetchSummary = async () => {
      if (!storeId) return;
      try {
        const data = await crewApi.getCrewRanksSummary(storeId, token);
        if (active) setSummary(data);
      } catch {
        // 機能無効(503)/未導出などは黙ってセクション非表示
        if (active) setSummary(null);
      }
    };
    fetchSummary();
    return () => {
      active = false;
    };
  }, [storeId, token]);

  // tier 表示順・件数（CrewManagement と共通のロジック・データソース）
  const orderedTiers = useCrewRankTiers(summary);

  if (!summary) return null;

  const adviceCount = summary.advice_count ?? 0;

  return (
    <Box sx={{ mb: 3 }}>
      {/* RECRUITING COPILOT 育成アドバイスのご提案（PDF p.3） */}
      <Paper
        sx={{
          mb: 2,
          p: 1.75,
          borderRadius: '12px',
          boxShadow: 'none',
          border: `1.5px solid ${COPILOT_PURPLE}33`,
          bgcolor: '#fbfaff',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              bgcolor: COPILOT_PILL,
              color: '#fff',
              borderRadius: '10px',
              px: 1,
              py: 0.3,
              mb: 0.75,
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 13, color: '#fff' }} />
            <Typography sx={{ fontWeight: 800, fontSize: '0.62rem', letterSpacing: '0.03em' }}>
              RECRUITING COPILOT
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 700, color: COPILOT_PURPLE }}>
            育成アドバイスのご提案{adviceCount > 0 ? `（${adviceCount}件）` : ''}
          </Typography>
        </Box>
        <Button
          onClick={() => navigate('/crew-management?advice=true')}
          sx={{
            flexShrink: 0,
            bgcolor: '#fff',
            color: COPILOT_PURPLE,
            border: `1px solid ${COPILOT_PURPLE}55`,
            borderRadius: '10px',
            fontWeight: 700,
            fontSize: '0.8rem',
            px: 2,
            boxShadow: 'none',
            '&:hover': { bgcolor: '#f3effc', boxShadow: 'none' },
          }}
        >
          確認する
        </Button>
      </Paper>

      {/* 👥 クルー管理 サマリ（ランク別人数 + 育成アドバイス。CrewManagement と同体裁） */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <SvgIcon
          component={SidebarCrewMgmtIcon}
          inheritViewBox
          sx={{ fontSize: '1.4rem', mr: 1, color: '#3c3c3c' }}
        />
        <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', color: '#3c3c3c' }}>
          クルー管理
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${orderedTiers.length}, minmax(0, 1fr))`,
          gap: 1,
        }}
      >
        {orderedTiers.map((t) => (
          <Paper
            key={t.code}
            onClick={() => navigate(`/crew-management?tier=${t.code}`)}
            sx={{
              px: 1,
              py: 1.25,
              borderRadius: '12px',
              boxShadow: 'none',
              border: '1px solid #e7e7e7',
              bgcolor: '#fff',
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': { bgcolor: '#f5f5f5' },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.25 }}>
              <RankStar tierCode={t.code} color={t.tier?.color} size={22} />
            </Box>
            <Typography variant="caption" sx={{ display: 'block', color: '#898989', fontWeight: 600, mb: 0.25, fontSize: '0.55rem' }}>
              {t.tier?.name ?? getRankTierLabel(t.code)}
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: PRIMARY_BLUE, lineHeight: 1 }}>
              {t.count}
            </Typography>
          </Paper>
        ))}

      </Box>
    </Box>
  );
};

export default CrewRankSummary;
